/**
 * MUTAGIM.SHOP — Wishlist Page (Phase 9)
 * Page de gestion des favoris avec partage
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Share2, Copy, Bell, BellOff, Trash2, ExternalLink, Lock, ShoppingCart } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useWishlist } from '@/contexts/WishlistContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function WishlistPage() {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { 
    wishlistItems, 
    loading, 
    shareToken, 
    isPublic, 
    toggleWishlist, 
    generateShareLink, 
    disableSharing,
    updateNotifications
  } = useWishlist();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const [shareLoading, setShareLoading] = useState(false);

  const handleShare = async () => {
    setShareLoading(true);
    const url = await generateShareLink();
    if (url) {
      const fullUrl = `${window.location.origin}${url}`;
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Lien copié dans le presse-papier !');
    }
    setShareLoading(false);
  };

  const handleCopyLink = async () => {
    if (shareToken) {
      const fullUrl = `${window.location.origin}/wishlist/${shareToken}`;
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Lien copié !');
    }
  };

  const handleAddToCart = async (item) => {
    if (item.product) {
      await addToCart(item.product.id);
      toast.success(`${item.product.name[i18n.language] || item.product.name.fr} ajouté au panier`);
    }
  };

  // Si non connecté
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-mutagim-dark flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md p-8">
            <Heart className="w-16 h-16 mx-auto mb-4 text-mutagim-red" />
            <h1 className="text-2xl font-bold text-white mb-2">Mes Favoris</h1>
            <p className="text-gray-400 mb-6">
              Connectez-vous pour voir et gérer votre liste de favoris
            </p>
            <Button className="bg-mutagim-red hover:bg-red-700">
              Se connecter
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mutagim-dark flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Heart className="w-8 h-8 text-mutagim-red fill-mutagim-red" />
              Mes Favoris
            </h1>
            <p className="text-gray-400 mt-1">
              {wishlistItems.length} produit{wishlistItems.length > 1 ? 's' : ''} dans votre liste
            </p>
          </div>
          
          {/* Actions de partage */}
          <div className="flex items-center gap-3">
            {isPublic && shareToken ? (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleCopyLink}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copier le lien
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={disableSharing}
                  className="text-gray-400 hover:text-red-400"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Rendre privé
                </Button>
              </>
            ) : (
              <Button 
                onClick={handleShare}
                disabled={shareLoading || wishlistItems.length === 0}
                className="bg-mutagim-red hover:bg-red-700"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {shareLoading ? 'Génération...' : 'Partager ma liste'}
              </Button>
            )}
          </div>
        </div>

        {/* Liste vide */}
        {wishlistItems.length === 0 && !loading && (
          <div className="text-center py-16">
            <Heart className="w-20 h-20 mx-auto mb-4 text-gray-700" />
            <h2 className="text-xl font-semibold text-white mb-2">Votre liste est vide</h2>
            <p className="text-gray-400 mb-6">
              Ajoutez des produits en cliquant sur le ❤️
            </p>
            <Link to="/products">
              <Button className="bg-mutagim-red hover:bg-red-700">
                Découvrir nos produits
              </Button>
            </Link>
          </div>
        )}

        {/* Liste des favoris */}
        {wishlistItems.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wishlistItems.map((item) => (
              <WishlistItemCard 
                key={item.id}
                item={item}
                onRemove={() => toggleWishlist(item.product_id)}
                onAddToCart={() => handleAddToCart(item)}
                onUpdateNotifications={(settings) => updateNotifications(item.product_id, settings)}
                formatPrice={formatPrice}
                lang={i18n.language}
              />
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-900 rounded-lg p-4 animate-pulse">
                <div className="aspect-square bg-gray-800 rounded-lg mb-4" />
                <div className="h-4 bg-gray-800 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-800 rounded w-1/2" />
              </div>
            ))}
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
}

// Composant carte item wishlist
function WishlistItemCard({ item, onRemove, onAddToCart, onUpdateNotifications, formatPrice, lang }) {
  const [showNotifications, setShowNotifications] = useState(false);
  const product = item.product;
  
  if (!product) return null;

  const name = product.name?.[lang] || product.name?.fr || 'Produit';
  const image = Array.isArray(product.images) 
    ? (product.images[0] || '/placeholder-product.jpg')
    : (product.images?.main || '/placeholder-product.jpg');
  const isOutOfStock = product.stock === 0;
  const badge = product.badges?.primary_badge;

  return (
    <div className="bg-gray-900/80 rounded-lg overflow-hidden border border-gray-800 hover:border-mutagim-red/50 transition-all">
      {/* Image */}
      <Link to={`/product/${product.slug}`} className="block relative aspect-square">
        <img 
          src={image} 
          alt={name}
          className="w-full h-full object-cover"
        />
        {badge && (
          <span className={cn(
            "absolute top-2 start-2 px-2 py-1 text-xs font-bold text-white rounded-md",
            badge === 'new' && 'bg-green-500',
            badge === 'trending' && 'bg-orange-500',
            badge === 'almost_sold_out' && 'bg-red-600',
            badge === 'mutagim_pick' && 'bg-purple-500'
          )}>
            {badge === 'new' && 'NOUVEAU'}
            {badge === 'trending' && 'TENDANCE'}
            {badge === 'almost_sold_out' && 'PRESQUE ÉPUISÉ'}
            {badge === 'mutagim_pick' && 'SÉLECTION'}
          </span>
        )}
        {isOutOfStock && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="text-white font-semibold">Rupture de stock</span>
          </div>
        )}
      </Link>

      {/* Infos */}
      <div className="p-4">
        <Link to={`/product/${product.slug}`}>
          <h3 className="text-white font-medium hover:text-mutagim-red transition-colors line-clamp-2">
            {name}
          </h3>
        </Link>
        
        {/* Rating */}
        {product.stats?.average_rating > 0 && (
          <div className="flex items-center gap-1 mt-1 text-sm text-gray-400">
            <span className="text-yellow-400">★</span>
            <span>{product.stats.average_rating.toFixed(1)}</span>
            <span>({product.stats.reviews_count})</span>
          </div>
        )}

        {/* Prix */}
        <p className="text-lg font-bold text-mutagim-red mt-2">
          {formatPrice(product.pricing?.prices)}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-4">
          <Button 
            onClick={onAddToCart}
            disabled={isOutOfStock}
            className="flex-1 bg-mutagim-red hover:bg-red-700"
            size="sm"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
          
          <Button 
            variant="outline"
            size="icon"
            onClick={() => setShowNotifications(!showNotifications)}
            className="border-gray-700 text-gray-400 hover:text-white"
          >
            <Bell className="w-4 h-4" />
          </Button>
          
          <Button 
            variant="outline"
            size="icon"
            onClick={onRemove}
            className="border-gray-700 text-gray-400 hover:text-red-500 hover:border-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        {/* Panneau notifications */}
        {showNotifications && (
          <div className="mt-4 p-3 bg-gray-800 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Retour en stock</span>
              <Switch 
                checked={item.notify_back_in_stock}
                onCheckedChange={(checked) => onUpdateNotifications({ 
                  notify_back_in_stock: checked,
                  notify_price_drop: item.notify_price_drop
                })}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Baisse de prix</span>
              <Switch 
                checked={item.notify_price_drop}
                onCheckedChange={(checked) => onUpdateNotifications({
                  notify_back_in_stock: item.notify_back_in_stock,
                  notify_price_drop: checked
                })}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default WishlistPage;
